import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { IPricePoliciesEntity } from "../../../Services/api/price-policies/price-policies.interface";
import { ListPricePoliciesUseCase } from "../../../Usecase/PricePolicies/list-price-policies-usecase";
import { DestroyPricePoliciesUseCase } from "../../../Usecase/PricePolicies/destroy-price-policies-usecase";
import { ResyncPricePoliciesUseCase } from "../../../Usecase/PricePolicies/resync-price-policies-usecase";
import { IListSearchPayload } from "../../../Components/List/list.types";
import { fetchAllPricePolicies } from "../../../utils/fetch-all-price-policies";
import { Environment } from "../../../Config/constants";

const PAGE_SIZE = Number(Environment.OFFSET_REGISTERS) || 50;

interface IUseListPricePoliciesProps {
  listPricePoliciesUseCase: ListPricePoliciesUseCase
  destroyPricePoliciesUseCase: DestroyPricePoliciesUseCase
  resyncPricePoliciesUseCase: ResyncPricePoliciesUseCase
}

export function useListPricePolicies({
  listPricePoliciesUseCase,
  destroyPricePoliciesUseCase,
  resyncPricePoliciesUseCase,
}: IUseListPricePoliciesProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [page, setPage] = useState(1)
    const [allData, setAllData] = useState<IPricePoliciesEntity[]>([])
    const [currentSearch, setCurrentSearch] = useState('')
    const [pricePolicyToDestroy, setPricePolicyToDestroy] = useState<IPricePoliciesEntity>()
    const [pricePolicyToResync, setPricePolicyToResync] = useState<IPricePoliciesEntity>()

    const { showToast } = useToast()

    const fetchPolicies = useCallback(async (searchBy = '') => {
      const policies = await fetchAllPricePolicies(
        listPricePoliciesUseCase,
        searchBy || undefined,
      );
      setAllData(policies);
    }, [listPricePoliciesUseCase]);

    const listPolicies = useCallback(async ({ page: currentPage, searchBy = '' }: IListSearchPayload) => {
        try {
          setLoading(true)
          setLoadError(false)
          await fetchPolicies(searchBy)
          setPage(currentPage)
          setCurrentSearch(searchBy ?? '')
          setLoadError(false)
        } catch (error) {
          setLoadError(true)
          console.error("error listPolicies function: ", error)
          showToast('Erro ao carregar políticas de preço', 'error');
          setAllData([])
        } finally {
          setLoading(false);
        }
      }, [fetchPolicies, showToast]);

    const handleSearch = useCallback((payload: IListSearchPayload) => {
      listPolicies({ page: 1, searchBy: payload.searchBy ?? '' })
    }, [listPolicies]);

    const handlePageChange = useCallback((payload: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setPage(payload.page)
    }, []);

    const totalRegisters = allData.length

    const list = useMemo(() => {
      const rows = allData.map((policy) => ({
        ...policy,
        rulesCount: policy.rules?.length ?? 0,
      }))
      const start = (page - 1) * PAGE_SIZE
      return rows.slice(start, start + PAGE_SIZE)
    }, [allData, page])

    const handleDestroyPricePolicy = async () => {
      try {
        setLoading(true)

        const listResult = await destroyPricePoliciesUseCase.handle(pricePolicyToDestroy?.id || '');

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        showToast('Removido com sucesso.', 'success');
        setPricePolicyToDestroy(undefined)
        setPage(1)
        await fetchPolicies(currentSearch)
      } catch (error) {
        console.log("error handleDestroy function: ", error)
      } finally {
        setLoading(false);
      }
    }

    const handleResyncPricePolicy = async () => {
      try {
        setLoading(true)

        const listResult = await resyncPricePoliciesUseCase.handle(pricePolicyToResync?.id || '');

        if (listResult.isFailure) {
          showToast(String(listResult?.error?.error), 'error');
          return;
        }

        showToast('Re-sincronização iniciada com sucesso.', 'success');
        setPricePolicyToResync(undefined)
        await fetchPolicies(currentSearch)
      } catch (error) {
        console.log("error handleResyncPricePolicy function: ", error)
      } finally {
        setLoading(false);
      }
    }

    return {
        loading,
        loadError,
        listPolicies,
        list,
        totalRegisters,
        page,
        currentSearch,
        handleSearch,
        handlePageChange,
        pricePolicyToDestroy,
        setPricePolicyToDestroy,
        handleDestroyPricePolicy,
        pricePolicyToResync,
        setPricePolicyToResync,
        handleResyncPricePolicy,
    }
}
